
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { VitaContent, VitaGeneratedContent } from "@/types/wellness";

export function useVitaStorage() {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  /**
   * Save content generated by Vita to the appropriate dashboard section
   */
  const saveVitaContent = async (content: VitaContent): Promise<VitaGeneratedContent | null> => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to save content",
        type: "error"
      });
      return null;
    }

    setIsLoading(true);

    try {
      // Make sure we're using the correct column names that exist in the table
      const { data, error } = await supabase
        .from('user_notes')
        .insert({
          user_id: user.id,
          // Use 'type' instead of 'title' since the schema uses 'type'
          type: content.category,
          content: content.content
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Content Saved",
        description: `Successfully saved to your ${content.category} dashboard`,
        type: "success"
      });

      // Convert the user_notes record to VitaGeneratedContent format
      return {
        id: data.id,
        user_id: data.user_id,
        title: content.title, // Keep title in the return type for frontend display
        content: content.content,
        category: content.category,
        created_at: data.created_at,
        updated_at: data.updated_at
      };
    } catch (error: any) {
      console.error("Error saving Vita content:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save content",
        type: "error"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get all content generated by Vita for a specific category
   */
  const getVitaContentByCategory = async (category: string): Promise<VitaGeneratedContent[]> => {
    if (!user) return [];

    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('user_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', category)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Convert user_notes to VitaGeneratedContent format
      return (data || []).map(note => ({
        id: note.id,
        user_id: note.user_id,
        // Generate a title from the content if needed
        title: note.content ? note.content.substring(0, 50) + '...' : 'Note',
        content: note.content || '',
        category: note.type as any,
        created_at: note.created_at,
        updated_at: note.updated_at
      }));
    } catch (error: any) {
      console.error("Error fetching Vita content:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch content",
        type: "error"
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a specific Vita content item
   */
  const deleteVitaContent = async (contentId: string): Promise<boolean> => {
    if (!user) return false;

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('user_notes')
        .delete()
        .eq('id', contentId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Content Deleted",
        description: "Successfully removed the content",
        type: "success"
      });

      return true;
    } catch (error: any) {
      console.error("Error deleting Vita content:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete content",
        type: "error"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    saveVitaContent,
    getVitaContentByCategory,
    deleteVitaContent,
    isLoading
  };
}
